import { useMemo } from "react";

import type {
  AgBarSeriesOptions,
  AgCartesianChartOptions,
  AgChartOptions,
} from "ag-charts-community";

import {
  AgChartPanel,
  cinchAgTheme,
  makeValueFormatter,
  resolveCssColor,
  resolveToken,
  useIsDark,
} from "@/components/cinch/charts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import type { CashFlowWeek } from "../api";
import {
  currentWeek,
  fmtUsd,
  monthLabel,
  periodWaterfall,
  rollupByMonth,
  waterfallRows,
  weekLabel,
  weeksEndingInMonth,
  weeksEndingInQuarter,
  weekWaterfall,
  type WaterfallRow,
  type WaterfallStep,
} from "../lib";

const usdCompact = makeValueFormatter("usd_compact");

/** Hand-rolled waterfall on AG Charts Community: invisible offset segments +
 *  colored bars, stacked (see {@link waterfallRows}). Start/End bars use the
 *  neutral baseline color; movements are brand (up) / danger (down) — the
 *  bridge style of the 13W report. */
function waterfallOptions(steps: WaterfallStep[], isDark: boolean): AgChartOptions {
  const rows = waterfallRows(steps);
  const brand = resolveToken("brand");
  const danger = resolveToken("danger");
  const baseline = resolveCssColor("hsl(var(--muted-foreground))");

  const colorOf = (row: WaterfallRow) =>
    row.kind === "delta" ? (row.shown >= 0 ? brand : danger) : baseline;

  const offsetSeries = (yKey: string): AgBarSeriesOptions => ({
    type: "bar",
    xKey: "label",
    yKey,
    stacked: true,
    fill: "transparent",
    stroke: "transparent",
    tooltip: { enabled: false },
  });
  const shownText = (row: WaterfallRow) =>
    row.kind === "delta"
      ? `${row.shown >= 0 ? "+" : ""}${usdCompact(row.shown)}`
      : usdCompact(row.shown);

  const barSeries = (yKey: string): AgBarSeriesOptions => ({
    type: "bar",
    xKey: "label",
    yKey,
    stacked: true,
    itemStyler: (p) => {
      const c = colorOf(p.datum as WaterfallRow);
      return { fill: c, stroke: c };
    },
    // The bar's real (signed) value above each column. A zero-crossing bar
    // has both a pos and a neg segment — label only the pos one to avoid a
    // duplicate.
    label: {
      formatter: (p) => {
        const row = p.datum as WaterfallRow;
        if (yKey === "negBar" && row.posBar != null) return "";
        return shownText(row);
      },
      placement: "outside-end",
      fontSize: 11,
    },
    tooltip: {
      renderer: (p) => {
        const row = p.datum as WaterfallRow;
        const amount =
          row.kind === "delta"
            ? `${row.shown >= 0 ? "+" : ""}${fmtUsd(row.shown)}`
            : fmtUsd(row.shown);
        return { title: row.label.replace(/\n/g, " "), content: amount };
      },
    },
  });

  const opts: AgCartesianChartOptions = {
    theme: cinchAgTheme([brand, danger], isDark),
    background: { visible: false },
    animation: { enabled: false },
    data: rows,
    // Offsets first so they sit under the visible bars in each stack.
    series: [
      offsetSeries("posOffset"),
      barSeries("posBar"),
      offsetSeries("negOffset"),
      barSeries("negBar"),
    ],
    axes: {
      x: { type: "category", position: "bottom" },
      y: {
        type: "number",
        position: "left",
        label: { formatter: (p) => usdCompact(Number(p.value)) },
      },
    },
    legend: { enabled: false },
  };
  return opts as AgChartOptions;
}

function WaterfallCard({
  title,
  description,
  steps,
  isDark,
}: {
  title: string;
  description: string;
  steps: WaterfallStep[];
  isDark: boolean;
}) {
  const options = useMemo(() => waterfallOptions(steps, isDark), [steps, isDark]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {steps.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No weeks in this period for the selected run.
          </p>
        ) : (
          <AgChartPanel options={options} className="h-80" />
        )}
      </CardContent>
    </Card>
  );
}

/** Cash bridges for the current week, month, and quarter, built from the
 *  run's weekly flows (weeks bucketed by their week-ending date). */
export function WaterfallTab({ weeks }: { weeks: CashFlowWeek[] }) {
  const isDark = useIsDark();
  const today = new Date();

  const week = currentWeek(weeks, today);
  const monthWeeks = weeksEndingInMonth(weeks, today);
  const quarterMonths = rollupByMonth(weeksEndingInQuarter(weeks, today));

  const monthName = today.toLocaleDateString("en-US", { month: "long" });
  const quarter = `Q${Math.floor(today.getMonth() / 3) + 1} ${today.getFullYear()}`;

  return (
    <div className="flex flex-col gap-6">
      <WaterfallCard
        title={`Current week — WE ${week ? weekLabel(week.week_ending, true) : "—"}`}
        description="Opening cash, this week's inflows and outflows, and where the week ends."
        steps={week ? weekWaterfall(week) : []}
        isDark={isDark}
      />
      <WaterfallCard
        title={`Current month — ${monthName}`}
        description="One net-change bar per week ending in the month, bridging opening to ending cash."
        steps={periodWaterfall(monthWeeks, (w) => `WE ${weekLabel(w.week_ending)}`)}
        isDark={isDark}
      />
      <WaterfallCard
        title={`Current quarter — ${quarter}`}
        description="One net-change bar per month, bridging the quarter's opening cash to its end."
        steps={periodWaterfall(quarterMonths, (w) => monthLabel(w.week_ending))}
        isDark={isDark}
      />
    </div>
  );
}
